// One card, many `kind`s. The tool_result payload the agent emits
// carries `{ kind, data }` — this component dispatches on `kind` to a
// tight per-shape renderer, all rooted in the same Chakra Box so the
// visual language stays consistent across message-list children.
//
// New capability kinds should either land here (add a case) or fall
// through to GenericCard. An unknown kind keeps a short label, with the
// raw payload behind a collapsed Details toggle.

import { useEffect, useRef, useState } from 'react';
import { Box, VStack, HStack, Text, Badge, Image, Code, Divider, Wrap, WrapItem, SimpleGrid, Spinner, Progress, useToast } from '@chakra-ui/react';
import { CampaignMonitorCard } from './CampaignMonitorCard';
import { MarketingPlanCard } from './MarketingPlanCard';
import { isCampaignMonitor } from '../components/campaignMonitor/model';
import type { ToolResult } from './types';
import { apiJson } from '../auth/apiFetch';
// Same Product-Ads-parity components every other ad surface uses.
// Reused (not duplicated) so agent-listed ads render identically to
// Product Ads / UGC Ads / Campaign Detail — chrome preview, status
// badge, click → AdDetailModal with approve / regenerate / push-to-
// Meta / inspector jump.
import { AdThumbnail, AdDetailModal, resolveAndOpenMaster, type ExpansionAd } from '../pages/ProductAds';
import { toExpansionAd } from '../lib/expansionAd';
import { useBrand } from '../brand/BrandContext';
import { QcFailureReview, qcReviewDisplayUrl, type QcOverridePatch } from '../components/adChrome';

export function ResourceCard({ result }: { result: ToolResult }) {
  // ── Error / synthetic gate results ────────────────────────────
  if (!result.ok) {
    if (result.needsConfirmation)  return null;   // rendered by ConfirmationCard instead
    if (result.spendGuardBlocked)  return <SpendGuardBlockCard result={result} />;
    if (result.tier3PhraseBlocked) return null;   // ConfirmationCard again
    return (
      <Box p={3} borderWidth="1px" borderColor="red.300" bg="red.50" borderRadius="md">
        <Text fontSize="12px" color="red.700">
          <strong>Error</strong> — {String(result.error || result.reason || 'unknown failure')}
        </Text>
      </Box>
    );
  }

  const { kind, data } = result;
  switch (kind) {
    case 'productList':       return <ProductListCard data={data as any} />;
    case 'adList':            return <AdListCard data={data as any} />;
    case 'campaignList':      return <CampaignListCard data={data as any} />;
    case 'spendReport':       return <SpendReportCard data={data as any} />;
    case 'runStatus':         return <RunStatusCard data={data as any} />;
    case 'ad':                return <AdInspectCard data={data as any} />;
    case 'adUpdate':          return <AdUpdateCard data={data as any} />;
    case 'brandUpdate':       return <BrandUpdateCard data={data as any} />;
    case 'brandPersonasUpdate': return <BrandPersonasUpdateCard data={data as any} />;
    case 'campaignUpdate':    return <CampaignUpdateCard data={data as any} />;
    case 'adRegenerationStarted': return <RegenStartedCard data={data as any} />;
    case 'metaPushBatch':     return <MetaPushBatchCard data={data as any} />;
    case 'workflowResult':    return <WorkflowResultCard data={data as any} />;
    case 'workflowStarted':   return <WorkflowStartedCard data={data as any} />;
    case 'plan':              return null;   // ConfirmationCard renders plans
    case 'campaignMonitor':   return <CampaignMonitorCard payload={result} />;
    case 'marketingPlan':
    case 'marketingPlanList': return <MarketingPlanCard payload={result} />;
    case 'platformFormatList': return <PlatformFormatListCard data={data as any} />;
    case 'searchResults':     return <SearchResultsCard data={data as any} />;
    case 'lifestyleImageList': return <LifestyleImageListCard data={data as any} />;
    case 'lifestyleDecisions': return <LifestyleDecisionsCard data={data as any} />;
    case 'runCancel':         return <RunCancelCard data={data as any} />;
    case 'artifactUpdate':    return <ArtifactUpdateCard data={data as any} />;
    default:
      return isCampaignMonitor(result) ? <CampaignMonitorCard payload={result} /> : <GenericCard result={result} />;
  }
}

// ── Shared shell ──────────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box p={3} borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="brand.surface">
      {children}
    </Box>
  );
}

function CardHeader({ label, subLabel, badge }: { label: string; subLabel?: string; badge?: string }) {
  return (
    <HStack justify="space-between" mb={2}>
      <VStack align="flex-start" spacing={0}>
        <Text fontSize="11px" color="brand.muted" textTransform="uppercase" letterSpacing="0.5px">{label}</Text>
        {subLabel && <Text fontSize="14px" fontWeight="700">{subLabel}</Text>}
      </VStack>
      {badge && <Badge>{badge}</Badge>}
    </HStack>
  );
}

// ── Per-kind cards ────────────────────────────────────────────────

function ProductListCard({ data }: { data: { brand?: { name?: string }; filter?: string | null; total: number; sampleCount: number; products: any[] } }) {
  const filterLabel = data.filter ? ` missing ${data.filter.replace(/_/g, ' ')}` : '';
  return (
    <Card>
      <CardHeader
        label="Product list"
        subLabel={`${data.total} ${data.brand?.name || 'products'}${filterLabel}`}
        badge={data.sampleCount < data.total ? `showing ${data.sampleCount}` : undefined}
      />
      <VStack align="stretch" spacing={2} maxH="360px" overflowY="auto">
        {data.products.map((p) => (
          <HStack key={p._id} spacing={3} p={2} borderWidth="1px" borderColor="brand.border" borderRadius="sm">
            {p.imageUrl && <Image src={p.imageUrl} boxSize="40px" objectFit="cover" borderRadius="sm" fallbackSrc="" />}
            <VStack align="flex-start" spacing={0} flex="1" minW={0}>
              <Text fontSize="13px" fontWeight="600" noOfLines={1}>{p.title || '(untitled)'}</Text>
              <HStack spacing={2} fontSize="11px" color="brand.muted">
                {p.price != null && <Text>{p.currency || '$'}{p.price}</Text>}
                {p.rating != null && <Text>★ {p.rating}</Text>}
                {p.reviewsSource && <Badge fontSize="10px">{p.reviewsSource}</Badge>}
                {!p.hasLifestyle && <Badge fontSize="10px" colorScheme="orange">no lifestyle</Badge>}
              </HStack>
            </VStack>
          </HStack>
        ))}
      </VStack>
    </Card>
  );
}

function AdListCard({ data }: { data: { brand?: { name?: string }; window?: { hours: number }; filter?: { kind?: string | null; status?: string | null }; total: number; sampleCount: number; ads: any[] } }) {
  const { activeBrand } = useBrand();
  const toast = useToast();
  const [openAd, setOpenAd] = useState<ExpansionAd | null>(null);
  // Late-arrival guard for resolveAndOpenMaster's network fallback below —
  // same pattern Product Ads / Campaigns / UGC Ads already use.
  const openAdIdRef = useRef<string | null>(null);
  openAdIdRef.current = openAd?.adId ?? null;

  const filterBits: string[] = [];
  if (data.filter?.kind)   filterBits.push(data.filter.kind);
  if (data.filter?.status) filterBits.push(data.filter.status);
  const filterLabel = filterBits.length ? ` (${filterBits.join(', ')})` : '';
  const windowLabel = data.window?.hours != null ? ` in last ${data.window.hours}h` : '';
  return (
    <Card>
      <CardHeader
        label="Recent ads"
        subLabel={`${data.total} ads for ${data.brand?.name || 'brand'}${windowLabel}${filterLabel}`}
        badge={data.sampleCount < data.total ? `showing ${data.sampleCount}` : undefined}
      />
      {/* Grid geometry mirrors the Product Ads expansion. minmax(160px)
          gives AdThumbnail's chrome preview room to render CTA buttons
          and safe-zone gutters; the old 56px thumbs were too tight for
          chrome and left video thumbnails as bare placeholder rectangles
          when renderUrl was null on drafts. */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={2} maxH="480px" overflowY="auto">
        {data.ads.map((a) => {
          const exp = toExpansionAd(a);
          return (
            <Box key={a._id} onClick={() => setOpenAd(exp)} cursor="pointer">
              <AdThumbnail ad={exp} />
            </Box>
          );
        })}
      </SimpleGrid>
      {openAd && activeBrand?.id && (
        <AdDetailModal
          ad={openAd}
          brandId={activeBrand.id}
          onClose={() => setOpenAd(null)}
          onOpenMaster={(masterAdId) => {
            if (!activeBrand?.id) return;
            // This card's sample list is a capped agent-tool preview, not a
            // full per-product/campaign fetch — go straight to the
            // single-ad fetch fallback.
            const fromAdId = openAd?.adId;
            void resolveAndOpenMaster({
              masterAdId,
              brandId: activeBrand.id,
              siblingAds: null,
              isCurrent: () => openAdIdRef.current === fromAdId,
              setOpenAd,
              toast
            });
          }}
          onMutated={(updated) => {
            // Guard against a late copy-save / regen resolving after the
            // operator has closed or switched ads. Ref-based (not the
            // closed-over `openAd`, which is stale by the time an in-flight
            // mutation on a derivative resolves after the operator has
            // since opened its master in-place) — matches the pattern
            // Product Ads / UGC Ads / Campaign Detail use (adversarial
            // finding: this comment previously claimed parity it didn't
            // have — the other three sites use openAdIdRef, this one still
            // checked plain `openAd`).
            if (openAdIdRef.current !== updated.adId) return;
            setOpenAd(updated);
          }}
        />
      )}
    </Card>
  );
}

function CampaignListCard({ data }: { data: { brand?: { name?: string }; total: number; campaigns: any[] } }) {
  return (
    <Card>
      <CardHeader label="Campaigns" subLabel={`${data.total} for ${data.brand?.name || 'brand'}`} />
      <VStack align="stretch" spacing={2}>
        {data.campaigns.map((c) => (
          <HStack key={c._id} justify="space-between" p={2} borderWidth="1px" borderColor="brand.border" borderRadius="sm">
            <VStack align="flex-start" spacing={0} minW={0} flex="1">
              <Text fontSize="13px" fontWeight="600" noOfLines={1}>{c.name || '(unnamed)'}</Text>
              <HStack spacing={2} fontSize="11px" color="brand.muted">
                <Badge fontSize="10px">{c.platform}</Badge>
                {c.kind && <Badge fontSize="10px">{c.kind}</Badge>}
                {c.platformFormat && <Text>{c.platformFormat}</Text>}
              </HStack>
            </VStack>
            <VStack align="flex-end" spacing={0} fontSize="11px">
              {c.status && <Badge>{c.status}</Badge>}
              <Text color="brand.muted">{c.adsetCount} adsets</Text>
            </VStack>
          </HStack>
        ))}
      </VStack>
    </Card>
  );
}

function SpendReportCard({ data }: { data: { windowHours: number; totalUsd: number; totalCalls?: number; entries: any[]; note?: string } }) {
  return (
    <Card>
      <CardHeader label={`Spend (last ${data.windowHours}h)`} subLabel={`$${data.totalUsd.toFixed(2)}`} badge={data.totalCalls != null ? `${data.totalCalls} calls` : undefined} />
      {data.note && <Text fontSize="12px" color="brand.muted" mb={2}>{data.note}</Text>}
      <VStack align="stretch" spacing={1}>
        {data.entries.slice(0, 8).map((e, i) => (
          <HStack key={i} justify="space-between" fontSize="12px">
            <Text noOfLines={1}><Badge fontSize="10px" mr={1}>{e.provider}</Badge>{e.stage}</Text>
            <HStack spacing={2}>
              <Text color="brand.muted">{e.callCount}×</Text>
              <Text fontWeight="600">${e.costUsd.toFixed(4)}</Text>
            </HStack>
          </HStack>
        ))}
      </VStack>
    </Card>
  );
}

function RunStatusCard({ data }: { data: any }) {
  const rollup = data.adStatusRollup || {};
  return (
    <Card>
      <CardHeader label="Run status" subLabel={data.runId || String(data._id)} badge={data.status} />
      <HStack spacing={4} mb={2} fontSize="12px">
        <Text><strong>{data.succeeded}</strong> ok</Text>
        <Text><strong>{data.failed}</strong> failed</Text>
        <Text><strong>{data.skipped}</strong> skipped</Text>
        <Text color="brand.muted">of {data.total}</Text>
      </HStack>
      {Object.keys(rollup).length > 0 && (
        <Wrap spacing={2} mb={data.errors?.length ? 2 : 0}>
          {Object.entries(rollup).map(([status, count]) => (
            <WrapItem key={status}><Badge fontSize="10px">{status}: {String(count)}</Badge></WrapItem>
          ))}
        </Wrap>
      )}
      {data.errors?.length > 0 && (
        <>
          <Divider my={2} />
          <VStack align="stretch" spacing={1}>
            {data.errors.map((e: any, i: number) => (
              <Text key={i} fontSize="11px" color="red.700">
                #{e.index ?? '?'} [{e.stage || '?'}] {String(e.message || '').slice(0, 200)}
              </Text>
            ))}
          </VStack>
        </>
      )}
    </Card>
  );
}

function AdInspectCard({ data }: { data: any }) {
  const { activeBrand } = useBrand();
  const [overridePatch, setOverridePatch] = useState<QcOverridePatch | null>(null);
  const adId = data.adId || data.id || data._id;
  const status = overridePatch?.status ?? data.status;
  const preview = qcReviewDisplayUrl({
    kind: data.kind,
    photorealUrl: data.photorealUrl,
    renderUrl: data.renderUrl,
    visionQc: data.visionQc
  });
  const reviewAd = {
    adId: String(adId || ''),
    status: String(status || 'draft'),
    kind: data.kind ?? null,
    photorealUrl: data.photorealUrl ?? null,
    renderUrl: data.renderUrl ?? null,
    posterUrl: data.posterUrl ?? null,
    regenerating: !!data.regenerating,
    visionQc: data.visionQc ?? null,
    renderErrorMessage: data.renderErrorMessage ?? null,
    failure: data.failure ?? null,
    qcOverridden: overridePatch?.qcOverridden ?? !!data.qcOverridden,
    qcOverriddenAt: overridePatch?.qcOverriddenAt ?? data.qcOverriddenAt ?? null,
    qcOverriddenBy: overridePatch?.qcOverriddenBy ?? data.qcOverriddenBy ?? null,
    qcOverrideReason: overridePatch?.qcOverrideReason ?? data.qcOverrideReason ?? null,
    qcAdvisoryOutline: overridePatch?.qcAdvisoryOutline ?? data.qcAdvisoryOutline
  };
  return (
    <Card>
      <CardHeader label={`${data.kind} ad`} subLabel={data.template} badge={status} />
      <HStack spacing={4} mb={2} fontSize="12px">
        <Text>{data.aspectRatio}</Text>
        <Text color="brand.muted">{data.platformFormat}</Text>
        {data.regenerating && <Badge colorScheme="purple">regenerating</Badge>}
      </HStack>
      {preview && (
        <Image src={preview} maxH="240px" objectFit="contain" borderRadius="sm" mb={2} fallbackSrc="" />
      )}
      {activeBrand?.id && adId && (
        <QcFailureReview
          ad={reviewAd}
          brandId={activeBrand.id}
          isRegenerating={!!data.regenerating}
          onMutated={setOverridePatch}
          showRejectedPreview={false}
        />
      )}
      {data.video?.prompt && (
        <>
          <Text fontSize="11px" color="brand.muted" mb={1}>Video prompt</Text>
          <Code display="block" whiteSpace="pre-wrap" fontSize="11px" p={2}>
            {String(data.video.prompt).slice(0, 400)}
          </Code>
        </>
      )}
      {data.image?.promptPreview && (
        <>
          <Text fontSize="11px" color="brand.muted" mb={1}>Image prompt (preview)</Text>
          <Code display="block" whiteSpace="pre-wrap" fontSize="11px" p={2}>
            {data.image.promptPreview}
          </Code>
        </>
      )}
    </Card>
  );
}

function AdUpdateCard({ data }: { data: any }) {
  return (
    <Card>
      <CardHeader label="Ad updated" subLabel={data._id} badge={data.status || (data.cta ? 'cta' : undefined)} />
      {data.alreadyArchived && <Text fontSize="12px" color="brand.muted">Already archived — no change.</Text>}
      {data.priorStatus && data.status && !data.alreadyArchived && (
        <Text fontSize="12px">{data.priorStatus} → <strong>{data.status}</strong></Text>
      )}
      {data.cta && (
        <VStack align="stretch" spacing={1} fontSize="12px">
          {data.cta.ctaText !== data.priorCta?.ctaText && <Text><strong>text:</strong> "{data.cta.ctaText}"</Text>}
          {data.cta.ctaUrl  !== data.priorCta?.ctaUrl  && <Text><strong>url:</strong> {data.cta.ctaUrl}</Text>}
        </VStack>
      )}
      {data.cacheNote && <Text fontSize="11px" color="brand.muted" mt={2}>{data.cacheNote}</Text>}
    </Card>
  );
}

function BrandUpdateCard({ data }: { data: any }) {
  return (
    <Card>
      <CardHeader label="Brand updated" subLabel={data.name} />
      {data.noop
        ? <Text fontSize="12px" color="brand.muted">{data.note}</Text>
        : (
          <VStack align="stretch" spacing={1} fontSize="12px">
            <Text><strong>Tagline:</strong> "{data.tagline}"</Text>
            {data.priorTagline && <Text color="brand.muted">Was: "{data.priorTagline}"</Text>}
            {data.cacheNote && <Text fontSize="11px" color="brand.muted" mt={2}>{data.cacheNote}</Text>}
          </VStack>
        )
      }
    </Card>
  );
}

// Regeneration/generation card that polls the Ad until the render
// lands, then shows the final image inline. Mirrors the workflow-
// progress live-tile UX for a SINGLE ad: placeholder skeleton up
// front, transitions to the rendered image when GET /ads/:id shows
// a fresh renderUrl and regenerating flips false. Failure states
// (Ad.status === 'failed' or renderError present) surface with the
// reason.
//
// Adgen owns the actual render (out of process), and the mint-time
// tool result doesn't wait for it. Client-side polling is how the
// chat gets the "streamed" feel for a workflow that has no SSE
// bridge from the render pipeline back to the agent HTTP connection.
function RegenStartedCard({ data }: { data: {
  _id: string;
  brand?: { _id?: string; name?: string };
  kind?: string;
  mode?: string;
  priorStatus?: string | null;
  regenerating?: boolean;
  note?: string;
} }) {
  const brandId = data.brand?._id || null;
  const adId = data._id;
  const [ad, setAd] = useState<AdPollShape | null>(null);
  const [elapsedS, setElapsedS] = useState(0);
  const startedRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!brandId || !adId) return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    // Wall-clock ticker for the elapsed-time display. 1s cadence so
    // the operator sees the seconds move; independent of the poll
    // interval which is much slower.
    const clockId = setInterval(() => {
      setElapsedS(Math.floor((Date.now() - startedRef.current) / 1000));
    }, 1000);

    const tick = async () => {
      try {
        const res = await apiJson<{ ad: AdPollShape }>(`/api/ads/${adId}?brandId=${encodeURIComponent(brandId)}`);
        if (cancelled) return;
        setAd(res.ad);
        // Stop polling on terminal states: render landed OR ad failed.
        // regenerating:false + fresh renderUrl = happy path.
        // status:'failed' = terminal failure.
        // Deadline safety: cap at 15 min so a genuinely-hung render
        // doesn't poll forever.
        const terminal = res.ad?.status === 'failed'
                      || (res.ad?.regenerating === false && !!res.ad?.renderUrl);
        const hitDeadline = (Date.now() - startedRef.current) > 15 * 60 * 1000;
        if (terminal || hitDeadline) {
          if (intervalId) clearInterval(intervalId);
          intervalId = null;
        }
      } catch { /* transient poll error — keep trying */ }
    };
    void tick();
    intervalId = setInterval(() => { void tick(); }, 4000);
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      clearInterval(clockId);
    };
  }, [adId, brandId]);

  const inFlight = !ad || (ad.regenerating !== false && ad.status !== 'failed');
  const terminalOk = !!ad && ad.regenerating === false && !!ad.renderUrl && ad.status !== 'failed';
  const terminalFail = !!ad && ad.status === 'failed';
  const kindLabel = data.kind === 'video' ? 'video' : 'image';
  const isVideo = data.kind === 'video';

  return (
    <Card>
      <CardHeader
        label={terminalOk ? `Regenerated ${kindLabel}` : terminalFail ? 'Regeneration failed' : `Regenerating ${kindLabel}`}
        subLabel={adId}
        badge={inFlight ? 'in flight' : (terminalOk ? 'ready' : terminalFail ? 'failed' : undefined)}
      />
      <HStack spacing={4} fontSize="11px" color="brand.muted" mb={2}>
        {data.mode && <Badge fontSize="10px" variant="subtle">{data.mode}</Badge>}
        {(data.priorStatus || ad?.status) && (
          <Text>status: <strong>{ad?.status || data.priorStatus}</strong></Text>
        )}
        {inFlight && <Text>elapsed {elapsedS}s</Text>}
      </HStack>

      <Box>
        {inFlight && (
          <Box
            aspectRatio={isVideo ? 16/9 : 1}
            borderRadius="md"
            borderWidth="1px"
            borderColor="brand.border"
            bg="brand.canvas"
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ animation: 'agent-ad-pulse 1.6s ease-in-out infinite', '@keyframes agent-ad-pulse': { '50%': { opacity: 0.5 } } }}
          >
            <VStack spacing={2}>
              <Spinner size="sm" />
              <Text fontSize="11px" color="brand.muted">
                Generating {kindLabel}… ({elapsedS}s)
              </Text>
              {isVideo && (
                <Text fontSize="10px" color="brand.muted">
                  Video typically takes 2-5 minutes
                </Text>
              )}
            </VStack>
          </Box>
        )}
        {terminalOk && ad.renderUrl && (
          <Box
            borderRadius="md"
            overflow="hidden"
            borderWidth="1px"
            borderColor="brand.border"
            cursor="pointer"
            onClick={() => window.open(ad.renderUrl!, '_blank', 'noopener,noreferrer')}
            _hover={{ borderColor: 'purple.400' }}
          >
            {isVideo
              ? <Box as="video" src={ad.renderUrl} controls poster={ad.posterUrl || undefined} w="100%" />
              : <Image src={ad.renderUrl} w="100%" fallbackSrc="" />
            }
          </Box>
        )}
        {terminalFail && (
          <Box p={3} bg="red.50" borderRadius="md" borderWidth="1px" borderColor="red.200">
            <Text fontSize="12px" fontWeight="700" color="red.700" mb={1}>
              Render failed
            </Text>
            <Text fontSize="11px" color="red.700">
              {ad?.renderError?.message || ad?.renderError?.stage || 'Unknown failure — check the Ad detail page for the full error.'}
            </Text>
          </Box>
        )}
      </Box>
    </Card>
  );
}

type AdPollShape = {
  _id: string;
  status?: string;
  regenerating?: boolean;
  renderUrl?: string | null;
  posterUrl?: string | null;
  renderError?: { message?: string; stage?: string; at?: string } | null;
};

function MetaPushBatchCard({ data }: { data: any }) {
  return (
    <Card>
      <CardHeader label="Meta push" subLabel={`adset ${data.adsetId}`} badge={`${data.pushed}/${data.pushed + data.failed} ok`} />
      <VStack align="stretch" spacing={1} maxH="200px" overflowY="auto">
        {(data.perAd || []).slice(0, 10).map((r: any) => (
          <HStack key={r.adId} justify="space-between" fontSize="11px">
            <Text noOfLines={1}>{r.adId.slice(-8)}</Text>
            {r.ok
              ? <Badge fontSize="10px" colorScheme="green">synced</Badge>
              : <Text color="red.700" noOfLines={1} maxW="260px" title={r.error}>{r.error}</Text>
            }
          </HStack>
        ))}
      </VStack>
    </Card>
  );
}

function WorkflowResultCard({ data }: { data: any }) {
  const artifacts = Array.isArray(data.artifacts) ? data.artifacts : [];
  return (
    <Card>
      <CardHeader label="Workflow finished" subLabel={data.workflowId} badge={`${data.succeeded}/${data.totalSteps} ok`} />
      <HStack spacing={4} fontSize="12px" mb={2}>
        <Text><strong>{data.succeeded}</strong> succeeded</Text>
        {data.failed > 0 && <Text color="red.600"><strong>{data.failed}</strong> failed</Text>}
        {data.skippedNoUrl > 0 && <Text color="brand.muted">{data.skippedNoUrl} skipped</Text>}
        {data.actualSpendUsd != null && <Text>${data.actualSpendUsd.toFixed(2)}</Text>}
        <Text color="brand.muted">{Math.round((data.durationMs || 0) / 1000)}s</Text>
      </HStack>
      {data.failureReasons && Object.keys(data.failureReasons).length > 0 && (
        <Wrap spacing={2}>
          {Object.entries(data.failureReasons).map(([reason, count]) => (
            <WrapItem key={reason}>
              <Badge fontSize="10px" colorScheme="red">{reason}: {String(count)}</Badge>
            </WrapItem>
          ))}
        </Wrap>
      )}
      {data.note && <Text fontSize="11px" color="brand.muted" mt={2}>{data.note}</Text>}

      {/* Inline gallery — when a workflow produces artifacts with URLs
          (today: catalog.generateLifestyleImages returns `artifacts`
          per-successful cell), render them in the chat directly so the
          operator sees the OUTPUT, not just counts. Solves the 2026-09-16
          transcript case where the agent had to say "check the platform's
          UI to see the images". */}
      {artifacts.length > 0 && (
        <Box mt={3}>
          <Text fontSize="11px" color="brand.muted" mb={1.5} textTransform="uppercase" letterSpacing="0.05em">
            Generated ({artifacts.length})
          </Text>
          <ArtifactThumbGrid artifacts={artifacts} />
        </Box>
      )}
    </Card>
  );
}

// Async-workflow polling card. Rendered when the agent Tier 4
// dispatch runs `execute.async === true` and returns a runId
// immediately (see routes/agent.js + catalog.generateLifestyleImages
// kickoff()). Polls GET /brand/:id/lifestyle-generation-runs/:runId
// every 3s with ?includeArtifacts=true so tiles fill in as cells
// land — same UX as the sync workflow-progress live-tile grid, but
// pull-based so a 30s edge-proxy timeout can't kill the connection.
function WorkflowStartedCard({ data }: {
  data: {
    workflowId: string;
    runId: string;
    brand?: { _id?: string; name?: string } | null;
    status?: string;
    note?: string;
  };
}) {
  const brandId = data.brand?._id || null;
  const runId = data.runId;
  const toast = useToast();
  const [run, setRun] = useState<AsyncRunShape | null>(null);
  // Phase 3 — cancel-in-progress optimistic UI (reset on terminal poll).
  const [cancelling, setCancelling] = useState(false);
  // Phase 6 — fetched decisions, loaded on operator click after terminal.
  const [decisions, setDecisions] = useState<DecisionRow[] | null>(null);
  const [decisionsLoading, setDecisionsLoading] = useState(false);

  useEffect(() => {
    if (!brandId || !runId) return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const tick = async () => {
      try {
        const r = await apiJson<AsyncRunShape>(
          `/api/brand/${brandId}/lifestyle-generation-runs/${runId}?includeArtifacts=true`
        );
        if (cancelled) return;
        setRun(r);
        const terminal = r.status === 'complete' || r.status === 'interrupted' || r.status === 'failed';
        if (terminal && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } catch { /* transient — keep polling */ }
    };
    void tick();
    intervalId = setInterval(() => { void tick(); }, 3000);
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [brandId, runId]);

  const status = run?.status || data.status || 'queued';
  const plannedCount = run?.plannedCount || 0;
  const processed = run?.processed || 0;
  const succeeded = run?.succeeded || 0;
  const failed = run?.failed || 0;
  const pct = plannedCount > 0 ? Math.round((processed / plannedCount) * 100) : 0;
  const terminal = status === 'complete' || status === 'interrupted' || status === 'failed';
  const cancelable = !!run?.cancelable && !run?.cancelRequestedAt;
  const cancelPending = !!run?.cancelRequestedAt && !terminal;
  const artifacts = Array.isArray(run?.artifacts) ? run.artifacts : [];
  // Filter to only ready cells for the tile grid (failed ones surface
  // via the count / reasons summary; showing red placeholder tiles per
  // failure was noisy for large runs).
  const readyArtifacts = artifacts.filter(a => a.status === 'ready' && a.imageUrl);
  // Phase 6 — count of cells whose policy decisions warrant human
  // review (escalated or droppedByPolicy). Only shown post-terminal
  // so the "Review decisions" button isn't noisy mid-run.
  const flaggedCount = terminal
    ? readyArtifacts.filter(a => a.visionQc && (a.visionQc.escalated || a.visionQc.droppedByPolicy)).length
    : 0;
  const hasDecisions = terminal && readyArtifacts.some(a => (a.visionQc?.decisionCount || 0) > 0);

  const requestCancel = async () => {
    if (!brandId || !runId || cancelling || cancelPending) return;
    setCancelling(true);
    try {
      await apiJson(`/api/brand/${brandId}/lifestyle-generation-runs/${runId}/cancel`, { method: 'POST' });
      toast({ title: 'Cancel requested', description: 'In-flight cells will complete; new ones won\'t start.', status: 'info', duration: 3500 });
    } catch (err: unknown) {
      toast({ title: 'Cancel failed', description: err instanceof Error ? err.message : 'Unknown error', status: 'error', duration: 5000 });
    } finally {
      setCancelling(false);
    }
  };

  const loadDecisions = async () => {
    if (!brandId || !runId || decisionsLoading) return;
    setDecisionsLoading(true);
    try {
      const r = await apiJson<{ decisions: DecisionRow[] }>(`/api/brand/${brandId}/lifestyle-decisions?runId=${runId}`);
      setDecisions(r.decisions || []);
    } catch (err: unknown) {
      toast({ title: 'Decisions load failed', description: err instanceof Error ? err.message : 'Unknown error', status: 'error', duration: 5000 });
    } finally {
      setDecisionsLoading(false);
    }
  };

  const revertAttempt = async (artifactId: string, attempt: number) => {
    if (!brandId) return;
    try {
      await apiJson(`/api/brand/${brandId}/lifestyle-artifacts/${artifactId}/revert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attempt }),
      });
      toast({ title: 'Reverted', description: `Artifact rolled back to attempt #${attempt}.`, status: 'success', duration: 3500 });
      // Refetch decisions so the policy-overlay flags show as cleared.
      await loadDecisions();
    } catch (err: unknown) {
      toast({ title: 'Revert failed', description: err instanceof Error ? err.message : 'Unknown error', status: 'error', duration: 5000 });
    }
  };

  return (
    <Card>
      <HStack justify="space-between" align="flex-start">
        <CardHeader
          label={terminal ? 'Generation complete' : status === 'queued' ? 'Queued' : (cancelPending ? 'Cancelling…' : 'Generating')}
          subLabel={data.brand?.name || data.workflowId}
          badge={
            terminal
              ? (status === 'complete' ? `${succeeded}/${plannedCount} ok` : status)
              : `${processed}/${plannedCount || '?'}`
          }
        />
        {(cancelable || cancelPending) && (
          <Box
            as="button"
            onClick={cancelable ? requestCancel : undefined}
            disabled={cancelPending || cancelling}
            fontSize="11px"
            px={2}
            py={1}
            borderWidth="1px"
            borderColor={cancelPending ? 'orange.300' : 'brand.border'}
            borderRadius="sm"
            bg={cancelPending ? 'orange.50' : 'white'}
            color={cancelPending ? 'orange.700' : 'brand.muted'}
            cursor={cancelable && !cancelling ? 'pointer' : 'default'}
            _hover={cancelable && !cancelling ? { borderColor: 'red.400', color: 'red.600' } : undefined}
          >
            {cancelPending ? 'Cancelling…' : (cancelling ? 'Requesting…' : 'Cancel')}
          </Box>
        )}
      </HStack>
      {!terminal && (
        <VStack align="stretch" spacing={2} mb={2}>
          <HStack spacing={2} fontSize="11px" color="brand.muted">
            <Spinner size="xs" />
            <Text>
              {plannedCount > 0
                ? `${processed} / ${plannedCount} (${pct}%)`
                : 'Planning cells…'}
            </Text>
            {succeeded > 0 && <Text>· {succeeded} ok</Text>}
            {failed > 0    && <Text color="red.600">· {failed} failed</Text>}
            {run?.actualSpendUsd != null && <Text>· ${run.actualSpendUsd.toFixed(2)}</Text>}
          </HStack>
          {plannedCount > 0 && (
            <Progress
              value={pct}
              size="xs"
              colorScheme="purple"
              borderRadius="sm"
              isIndeterminate={status === 'queued'}
            />
          )}
          {run?.lastCellNote && (
            <Text fontSize="10px" color="brand.muted" noOfLines={1}>
              <em>Last:</em> {run.lastCellNote}
            </Text>
          )}
        </VStack>
      )}
      {terminal && (
        <HStack spacing={3} fontSize="12px" mb={2}>
          <Text><strong>{succeeded}</strong> succeeded</Text>
          {failed > 0 && <Text color="red.600"><strong>{failed}</strong> failed</Text>}
          {run?.actualSpendUsd != null && <Text color="brand.muted">${run.actualSpendUsd.toFixed(2)}</Text>}
        </HStack>
      )}
      {run?.failureReasons && Object.keys(run.failureReasons).length > 0 && (
        <Wrap spacing={2} mb={2}>
          {Object.entries(run.failureReasons).map(([reason, count]) => (
            <WrapItem key={reason}>
              <Badge fontSize="10px" colorScheme="red">{reason}: {String(count)}</Badge>
            </WrapItem>
          ))}
        </Wrap>
      )}
      {terminal && run?.note && <Text fontSize="11px" color="brand.muted" mb={2}>{run.note}</Text>}

      {(readyArtifacts.length > 0 || (!terminal && plannedCount > 0)) && (
        <Box mt={2}>
          {!terminal && (
            <Text fontSize="10px" color="brand.muted" mb={1} textTransform="uppercase" letterSpacing="0.05em">
              {readyArtifacts.length} of {plannedCount} landed
            </Text>
          )}
          <SimpleGrid columns={{ base: 3, sm: 4, md: 6 }} spacing={1.5}>
            {readyArtifacts.map((a, i) => {
              const vq = a.visionQc;
              // Phase 3/6 — overlay badge on tiles that need operator
              // attention. Priority: reverted > escalated > droppedByPolicy.
              const badge = vq?.revertedAt
                ? { bg: 'blue.500',   label: 'R',    title: 'Reverted by operator' }
                : vq?.escalated
                ? { bg: 'orange.500', label: '!',    title: 'Escalated for review' }
                : vq?.droppedByPolicy
                ? { bg: 'yellow.600', label: 'D',    title: 'Dropped by policy (QC fail, ship-as-is)' }
                : vq?.decisionCount
                ? { bg: 'purple.500', label: String(vq.decisionCount), title: `${vq.decisionCount} policy decision(s) made` }
                : null;
              return (
                <Box
                  key={i}
                  position="relative"
                  aspectRatio={1}
                  borderRadius="sm"
                  borderWidth="1px"
                  borderColor={vq?.escalated ? 'orange.400' : vq?.droppedByPolicy ? 'yellow.500' : 'brand.border'}
                  overflow="hidden"
                  cursor="pointer"
                  onClick={() => a.imageUrl && window.open(a.imageUrl, '_blank', 'noopener,noreferrer')}
                  title={`${a.personaName || 'Generic'} · ${a.productName || 'Product'}${a.variant ? ` · ${a.variant}` : ''}${vq?.decisionCount ? ` · ${vq.decisionCount} decision(s)` : ''}`}
                  _hover={{ borderColor: 'purple.400' }}
                >
                  <Image src={a.imageUrl || ''} objectFit="cover" w="100%" h="100%" fallbackSrc="" />
                  {badge && (
                    <Box
                      position="absolute"
                      top={0.5}
                      right={0.5}
                      bg={badge.bg}
                      color="white"
                      fontSize="9px"
                      fontWeight="700"
                      borderRadius="full"
                      minW="16px"
                      h="16px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      px={1}
                      title={badge.title}
                    >
                      {badge.label}
                    </Box>
                  )}
                </Box>
              );
            })}
            {!terminal && Array.from({ length: Math.max(0, plannedCount - readyArtifacts.length) }, (_, i) => (
              <Box
                key={`ph-${i}`}
                aspectRatio={1}
                borderRadius="sm"
                borderWidth="1px"
                borderColor="brand.border"
                bg="brand.canvas"
                sx={{ animation: 'agent-run-tile-pulse 1.6s ease-in-out infinite', '@keyframes agent-run-tile-pulse': { '50%': { opacity: 0.4 } } }}
              />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* Phase 6 — producer-agent decision audit. Shown post-terminal
          only, when the run actually involved policy decisions. One
          button → inline list → per-decision revert actions. */}
      {hasDecisions && (
        <Box mt={3} pt={3} borderTopWidth="1px" borderTopColor="brand.border">
          {!decisions && (
            <Box
              as="button"
              onClick={loadDecisions}
              disabled={decisionsLoading}
              fontSize="11px"
              px={2}
              py={1}
              borderWidth="1px"
              borderColor="brand.border"
              borderRadius="sm"
              bg="white"
              color="purple.700"
              cursor={decisionsLoading ? 'default' : 'pointer'}
              _hover={decisionsLoading ? undefined : { borderColor: 'purple.400' }}
            >
              {decisionsLoading
                ? 'Loading decisions…'
                : `Review ${flaggedCount > 0 ? `${flaggedCount} flagged + ` : ''}producer decisions`}
            </Box>
          )}
          {decisions && (
            <ProducerDecisionsInline decisions={decisions} onRevert={revertAttempt} />
          )}
        </Box>
      )}
    </Card>
  );
}

// Phase 6 — one row per decision, grouped by artifact so adjacent
// decisions from the same cell stay together. Each row surfaces the
// revert button when there's a discarded attempt to roll back to.
type DecisionRow = {
  artifactId:   string;
  productId:    string;
  personaName:  string | null;
  afterAttempt: number | null;
  decision:     'regen-same-seed' | 'regen-new-seed' | 'drop' | 'escalate' | null;
  reason:       string | null;
  source:       string | null;
  verdictSummary: string | null;
  finalEscalated:       boolean;
  finalDroppedByPolicy: boolean;
  finalPassed:          boolean;
  attemptsAvailableForRevert?: Array<{ attempt: number; renderUrl: string; summary: string | null; pass: boolean }>;
};

const DECISION_COLOR: Record<string, string> = {
  'regen-same-seed': 'blue',
  'regen-new-seed':  'purple',
  'drop':            'yellow',
  'escalate':        'orange',
};

function ProducerDecisionsInline({ decisions, onRevert }: {
  decisions: DecisionRow[];
  onRevert: (artifactId: string, attempt: number) => void;
}) {
  if (!decisions.length) {
    return <Text fontSize="11px" color="brand.muted">No producer-agent decisions on this run.</Text>;
  }
  // Group by artifact so a cell's two decisions sit together.
  const byArtifact = new Map<string, DecisionRow[]>();
  for (const d of decisions) {
    if (!byArtifact.has(d.artifactId)) byArtifact.set(d.artifactId, []);
    byArtifact.get(d.artifactId)!.push(d);
  }
  return (
    <VStack align="stretch" spacing={2} mt={1}>
      {Array.from(byArtifact.entries()).map(([artifactId, group]) => {
        const first = group[0];
        const avail = Array.isArray(group[group.length - 1].attemptsAvailableForRevert)
          ? group[group.length - 1].attemptsAvailableForRevert!
          : [];
        return (
          <Box key={artifactId} p={2} borderWidth="1px" borderColor="brand.border" borderRadius="sm" bg="brand.canvas">
            <HStack spacing={2} fontSize="11px" mb={1}>
              <Text fontWeight="600">{first.personaName || 'Generic'}</Text>
              {first.finalEscalated && <Badge colorScheme="orange" fontSize="9px">escalated</Badge>}
              {first.finalDroppedByPolicy && <Badge colorScheme="yellow" fontSize="9px">dropped</Badge>}
              {first.finalPassed && <Badge colorScheme="green" fontSize="9px">passed</Badge>}
            </HStack>
            {group.map((d, i) => (
              <HStack key={i} spacing={2} fontSize="10px" color="brand.muted">
                <Text>#{d.afterAttempt}</Text>
                <Badge colorScheme={DECISION_COLOR[d.decision || ''] || 'gray'} fontSize="9px">
                  {d.decision || '?'}
                </Badge>
                {d.source && d.source !== 'llm' && (
                  <Badge variant="outline" fontSize="9px">{d.source}</Badge>
                )}
                {d.reason && <Text noOfLines={1} flex="1">— {d.reason}</Text>}
              </HStack>
            ))}
            {avail.length > 0 && (
              <HStack spacing={1} mt={1.5}>
                <Text fontSize="10px" color="brand.muted">Revert to:</Text>
                {avail.map((at) => (
                  <Box
                    as="button"
                    key={at.attempt}
                    onClick={() => onRevert(artifactId, at.attempt)}
                    fontSize="10px"
                    px={1.5}
                    py={0.5}
                    borderWidth="1px"
                    borderColor="brand.border"
                    borderRadius="sm"
                    cursor="pointer"
                    _hover={{ borderColor: 'purple.400', color: 'purple.700' }}
                    title={at.summary || `attempt #${at.attempt}`}
                  >
                    attempt #{at.attempt}
                  </Box>
                ))}
              </HStack>
            )}
          </Box>
        );
      })}
    </VStack>
  );
}

// Phase 3 — compact card for the lifestyle.cancelRun capability result.
function RunCancelCard({ data }: {
  data: {
    runId: string;
    brand?: { name?: string } | null;
    status: string;
    summary?: string;
    note?: string;
  };
}) {
  return (
    <Card>
      <CardHeader
        label="Cancel requested"
        subLabel={data.brand?.name || data.runId}
        badge={data.status}
      />
      {data.summary && <Text fontSize="11px" color="brand.muted">{data.summary}</Text>}
      {data.note && <Text fontSize="11px" color="brand.muted" mt={1}>{data.note}</Text>}
    </Card>
  );
}

// Phase 6 — compact card for a top-level lifestyle.listDecisions
// capability result (when invoked outside a WorkflowStartedCard).
function LifestyleDecisionsCard({ data }: {
  data: {
    runId: string;
    brand?: { name?: string } | null;
    runStatus: string;
    totalDecisions: number;
    byDecision: Record<string, number>;
    decisions: DecisionRow[];
  };
}) {
  return (
    <Card>
      <CardHeader
        label="Producer-agent decisions"
        subLabel={data.brand?.name || data.runId}
        badge={`${data.totalDecisions} total`}
      />
      <Wrap spacing={2} mb={2}>
        {Object.entries(data.byDecision || {}).map(([k, v]) => (
          <WrapItem key={k}>
            <Badge colorScheme={DECISION_COLOR[k] || 'gray'} fontSize="10px">{k}: {v}</Badge>
          </WrapItem>
        ))}
      </Wrap>
      {data.decisions.length > 0 && (
        <ProducerDecisionsInline
          decisions={data.decisions}
          onRevert={() => {
            /* top-level card doesn't own apiJson base; revert is a
               separate agent capability dispatch, not a direct call */
          }}
        />
      )}
    </Card>
  );
}

// Phase 6 — compact card for the lifestyle.revertArtifact capability
// result (one-off dispatch via chat).
function ArtifactUpdateCard({ data }: {
  data: {
    artifactId: string;
    imageUrl?: string;
    revertedToAttempt?: number;
    note?: string;
  };
}) {
  return (
    <Card>
      <CardHeader
        label={data.revertedToAttempt != null ? `Reverted to attempt #${data.revertedToAttempt}` : 'Artifact updated'}
        subLabel={data.artifactId}
      />
      {data.note && <Text fontSize="11px" color="brand.muted">{data.note}</Text>}
      {data.imageUrl && (
        <Box mt={2} w="120px" borderRadius="sm" borderWidth="1px" borderColor="brand.border" overflow="hidden">
          <Image src={data.imageUrl} aspectRatio={1} objectFit="cover" />
        </Box>
      )}
    </Card>
  );
}

type AsyncArtifactVisionQc = {
  passed:          boolean;
  skipped:         boolean;
  escalated:       boolean;
  droppedByPolicy: boolean;
  attemptCount:    number;
  decisionCount:   number;
  revertedAt?:     string | null;
};

type AsyncRunShape = {
  runId: string;
  status: 'queued' | 'running' | 'complete' | 'interrupted' | 'failed';
  plannedCount: number;
  processed: number;
  succeeded: number;
  failed: number;
  actualSpendUsd?: number;
  lastCellNote?: string | null;
  note?: string | null;
  errorMessage?: string | null;
  failureReasons?: Record<string, number> | null;
  // Phase 3 — cooperative cancel
  cancelable?: boolean;
  cancelRequestedAt?: string | null;
  artifacts?: Array<{
    artifactId?: string;
    productId: string;
    productName?: string | null;
    personaId?: string | null;
    personaName?: string | null;
    variant?: string | null;
    status: 'ready' | 'failed';
    imageUrl?: string | null;
    errorMessage?: string | null;
    // Phase 3/6 — visionQc summary for tile overlay badges
    visionQc?: AsyncArtifactVisionQc | null;
  }> | null;
};

function LifestyleImageListCard({ data }: {
  data: {
    brand?: { name?: string };
    total: number;
    truncated?: boolean;
    artifacts: Array<{ productId: string; productName?: string | null; personaId?: string | null; personaName?: string | null; imageUrl: string; generatedAt?: string; artifactId?: string | null }>;
    filter?: { productIds?: string[] | null; personaIds?: string[] | null };
  };
}) {
  return (
    <Card>
      <CardHeader
        label="Lifestyle images"
        subLabel={data.brand?.name || 'brand'}
        badge={data.truncated ? `${data.total}+` : String(data.total)}
      />
      {data.total === 0 ? (
        <Text fontSize="12px" color="brand.muted">
          No lifestyle images match this filter.
        </Text>
      ) : (
        <ArtifactThumbGrid artifacts={data.artifacts} />
      )}
    </Card>
  );
}

// Shared thumb-grid renderer used by workflow results + list card.
// Click a thumb opens the image in a new tab (Cloudinary URL). Persona
// name + product name shown below as ultracompact captions.
function ArtifactThumbGrid({ artifacts }: {
  artifacts: Array<{ productName?: string | null; personaName?: string | null; imageUrl: string; artifactId?: string | null; productId?: string | null; personaId?: string | null }>;
}) {
  return (
    <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={2} maxH="360px" overflowY="auto">
      {artifacts.map((a, i) => (
        <Box
          key={a.artifactId || `${a.productId || i}-${a.personaId || i}`}
          borderWidth="1px"
          borderColor="brand.border"
          borderRadius="sm"
          overflow="hidden"
          cursor="pointer"
          onClick={() => window.open(a.imageUrl, '_blank', 'noopener,noreferrer')}
          title={`${a.personaName || 'Generic'} · ${a.productName || 'Unknown product'}`}
          _hover={{ borderColor: 'purple.400' }}
        >
          <Image src={a.imageUrl} aspectRatio={1} objectFit="cover" w="100%" fallbackSrc="" />
          <Box px={1} py={0.5} bg="brand.surface">
            <Text fontSize="9px" fontWeight="600" noOfLines={1}>
              {a.personaName || 'Generic'}
            </Text>
            <Text fontSize="9px" color="brand.muted" noOfLines={1}>
              {a.productName || '(untitled)'}
            </Text>
          </Box>
        </Box>
      ))}
    </SimpleGrid>
  );
}

function PlatformFormatListCard({ data }: { data: any }) {
  return (
    <Card>
      <CardHeader label="Ad surfaces" subLabel={`${data.totalFormats} formats across ${data.platforms?.length || 0} platforms`} />
      <VStack align="stretch" spacing={2} maxH="360px" overflowY="auto">
        {(data.formats || []).map((f: any) => (
          <Box key={f.key} p={2} borderWidth="1px" borderColor="brand.border" borderRadius="sm">
            <HStack justify="space-between">
              <Text fontSize="13px" fontWeight="600">{f.label}</Text>
              <HStack spacing={1}>
                <Badge fontSize="10px">{f.platform}</Badge>
                <Badge fontSize="10px">{f.aspectRatio}</Badge>
                {(f.kinds || []).map((k: string) => <Badge key={k} fontSize="10px">{k}</Badge>)}
              </HStack>
            </HStack>
            {f.creativeBrief && (
              <Text fontSize="11px" color="brand.muted" mt={1}>{f.creativeBrief}</Text>
            )}
          </Box>
        ))}
      </VStack>
    </Card>
  );
}

// Generic-shape card for the 6 campaign.* executors that all emit
// kind:'campaignUpdate' with heterogeneous data shapes. Prints the
// campaign name + a short chip summary derived from whatever fields
// are present + the note. Beats the raw-JSON fallback across the
// whole campaign capability surface.
function CampaignUpdateCard({ data }: { data: any }) {
  const chips: Array<{ label: string; value: string }> = [];
  if (typeof data?.count === 'number') chips.push({ label: 'total', value: String(data.count) });
  if (typeof data?.added === 'number') chips.push({ label: 'added', value: String(data.added) });
  if (typeof data?.removed === 'number') chips.push({ label: 'removed', value: String(data.removed) });
  if (typeof data?.total === 'number') chips.push({ label: 'total after', value: String(data.total) });
  if (Array.isArray(data?.droppedProductIds) && data.droppedProductIds.length) {
    chips.push({ label: 'dropped products', value: String(data.droppedProductIds.length) });
  }
  if (Array.isArray(data?.droppedMediaIds) && data.droppedMediaIds.length) {
    chips.push({ label: 'dropped media', value: String(data.droppedMediaIds.length) });
  }
  if (Array.isArray(data?.audienceSegments)) {
    chips.push({ label: 'personas', value: String(data.audienceSegments.length) });
  }
  return (
    <Card>
      <CardHeader label="Campaign updated" subLabel={data?.name || data?._id} />
      {chips.length > 0 && (
        <Wrap spacing={2} mb={data?.note ? 2 : 0}>
          {chips.map((c, i) => (
            <WrapItem key={i}>
              <Badge fontSize="10px" variant="subtle">{c.label}: {c.value}</Badge>
            </WrapItem>
          ))}
        </Wrap>
      )}
      {data?.note && <Text fontSize="12px" color="brand.muted">{data.note}</Text>}
    </Card>
  );
}

function BrandPersonasUpdateCard({ data }: {
  data: {
    _id: string; name?: string;
    appended: number; total: number; curatedFieldsPinned?: boolean;
    personas: Array<{ name: string; description?: string; interests?: string[]; painPoints?: string[]; toneHint?: string; avatarUrl?: string }>;
  }
}) {
  return (
    <Card>
      <CardHeader
        label={data.appended > 0 ? `${data.appended} persona${data.appended === 1 ? '' : 's'} generated` : 'Personas updated'}
        subLabel={data.name}
        badge={`${data.total} of 6`}
      />
      <VStack align="stretch" spacing={2}>
        {data.personas.map((p, i) => (
          <Box key={i} p={2} borderWidth="1px" borderColor="brand.border" borderRadius="sm">
            <HStack justify="space-between" align="flex-start" spacing={2}>
              <Text fontSize="13px" fontWeight="700">{p.name}</Text>
              {p.avatarUrl && (
                <Image src={p.avatarUrl} boxSize="24px" borderRadius="sm" objectFit="cover" fallbackSrc="" />
              )}
            </HStack>
            {p.description && (
              <Text fontSize="12px" color="brand.muted" mt={0.5}>{p.description}</Text>
            )}
            {(p.interests?.length || p.painPoints?.length) && (
              <Wrap spacing={1} mt={1.5}>
                {(p.interests || []).slice(0, 5).map((v, j) => (
                  <WrapItem key={`i-${j}`}><Badge fontSize="10px" variant="subtle" colorScheme="purple">{v}</Badge></WrapItem>
                ))}
                {(p.painPoints || []).slice(0, 4).map((v, j) => (
                  <WrapItem key={`p-${j}`}><Badge fontSize="10px" variant="subtle" colorScheme="orange">{v}</Badge></WrapItem>
                ))}
              </Wrap>
            )}
          </Box>
        ))}
      </VStack>
      {data.curatedFieldsPinned && (
        <Text fontSize="11px" color="brand.muted" mt={2}>
          <em>Pinned in curatedFields — a future brand-enrichment refresh will not overwrite these.</em>
        </Text>
      )}
    </Card>
  );
}

function SearchResultsCard({ data }: {
  data: {
    query: string;
    resourceTypes?: string[];
    counts:    { brand: number | null; product: number | null; campaign: number | null; ad: number | null };
    truncated: { brand: boolean; product: boolean; campaign: boolean; ad: boolean };
    results:   {
      brand:    Array<{ _id: string; name: string; websiteUrl?: string | null; status?: string | null }> | null;
      product:  Array<{ _id: string; brandId: string; title: string; imageUrl?: string | null; price?: number | null; currency?: string | null }> | null;
      campaign: Array<{ _id: string; name: string; status?: string | null; kind?: string | null }> | null;
      ad:       Array<{ _id: string; title?: string | null; headline?: string | null; ctaText?: string | null; status?: string | null; kind?: string | null; renderUrl?: string | null }> | null;
    };
    note?: string;
  }
}) {
  const legs: Array<{ type: 'brand' | 'product' | 'campaign' | 'ad'; label: string }> = [
    { type: 'brand',    label: 'Brands' },
    { type: 'product',  label: 'Products' },
    { type: 'campaign', label: 'Campaigns' },
    { type: 'ad',       label: 'Ads' }
  ];
  const anyResult = legs.some(l => (data.counts[l.type] ?? 0) > 0);
  const totalCount = legs.reduce((s, l) => s + (data.counts[l.type] ?? 0), 0);

  return (
    <Card>
      <CardHeader
        label="Search"
        subLabel={`"${data.query}"`}
        badge={anyResult ? `${totalCount} match${totalCount === 1 ? '' : 'es'}` : 'no matches'}
      />
      {!anyResult && (
        <Text fontSize="12px" color="brand.muted">
          Nothing found across the requested resource types. Try fewer or more distinctive keywords.
        </Text>
      )}
      <VStack align="stretch" spacing={3}>
        {legs.map(l => {
          const rows = data.results[l.type];
          const count = data.counts[l.type];
          if (count == null) return null;               // leg wasn't queried
          if (!rows || rows.length === 0) return null;  // queried, zero hits — skip to reduce noise
          return (
            <Box key={l.type}>
              <HStack mb={1.5} spacing={2}>
                <Text fontSize="11px" fontWeight="600" textTransform="uppercase" letterSpacing="0.04em" color="brand.muted">
                  {l.label}
                </Text>
                <Badge fontSize="10px">
                  {count}{data.truncated[l.type] ? '+' : ''}
                </Badge>
              </HStack>
              <VStack align="stretch" spacing={1.5}>
                {l.type === 'product' && (rows as any[]).map(r => (
                  <HStack key={r._id} spacing={2.5} p={1.5} borderWidth="1px" borderColor="brand.border" borderRadius="sm">
                    {r.imageUrl && <Image src={r.imageUrl} boxSize="36px" objectFit="cover" borderRadius="sm" fallbackSrc="" />}
                    <VStack align="flex-start" spacing={0} flex="1" minW={0}>
                      <Text fontSize="12px" fontWeight="600" noOfLines={1}>{r.title || '(untitled)'}</Text>
                      <HStack spacing={2} fontSize="10px" color="brand.muted">
                        {r.price != null && <Text>{r.currency || '$'}{r.price}</Text>}
                        <Code fontSize="10px" bg="transparent" p={0}>{String(r._id).slice(-8)}</Code>
                      </HStack>
                    </VStack>
                  </HStack>
                ))}
                {l.type === 'brand' && (rows as any[]).map(r => (
                  <HStack key={r._id} justify="space-between" p={1.5} borderWidth="1px" borderColor="brand.border" borderRadius="sm">
                    <VStack align="flex-start" spacing={0} minW={0} flex="1">
                      <Text fontSize="12px" fontWeight="600" noOfLines={1}>{r.name}</Text>
                      {r.websiteUrl && <Text fontSize="10px" color="brand.muted" noOfLines={1}>{r.websiteUrl}</Text>}
                    </VStack>
                    {r.status && <Badge fontSize="10px">{r.status}</Badge>}
                  </HStack>
                ))}
                {l.type === 'campaign' && (rows as any[]).map(r => (
                  <HStack key={r._id} justify="space-between" p={1.5} borderWidth="1px" borderColor="brand.border" borderRadius="sm">
                    <VStack align="flex-start" spacing={0} minW={0} flex="1">
                      <Text fontSize="12px" fontWeight="600" noOfLines={1}>{r.name || '(unnamed)'}</Text>
                      <HStack spacing={1.5} fontSize="10px" color="brand.muted">
                        {r.kind && <Badge fontSize="10px">{r.kind}</Badge>}
                        {r.status && <Badge fontSize="10px">{r.status}</Badge>}
                      </HStack>
                    </VStack>
                  </HStack>
                ))}
                {l.type === 'ad' && (rows as any[]).map(r => (
                  <HStack key={r._id} spacing={2.5} p={1.5} borderWidth="1px" borderColor="brand.border" borderRadius="sm">
                    {r.renderUrl && <Image src={r.renderUrl} boxSize="36px" objectFit="cover" borderRadius="sm" fallbackSrc="" />}
                    <VStack align="flex-start" spacing={0} flex="1" minW={0}>
                      <Text fontSize="12px" fontWeight="600" noOfLines={1}>
                        {r.title || r.headline || '(untitled ad)'}
                      </Text>
                      <HStack spacing={1.5} fontSize="10px" color="brand.muted">
                        {r.kind && <Badge fontSize="10px">{r.kind}</Badge>}
                        {r.status && <Badge fontSize="10px">{r.status}</Badge>}
                      </HStack>
                    </VStack>
                  </HStack>
                ))}
              </VStack>
            </Box>
          );
        })}
      </VStack>
    </Card>
  );
}

function SpendGuardBlockCard({ result }: { result: any }) {
  return (
    <Box p={3} borderWidth="1px" borderColor="orange.300" bg="orange.50" borderRadius="md">
      <Text fontSize="12px" fontWeight="700" color="orange.700" mb={1}>Spend cap blocked this action</Text>
      <Text fontSize="12px" color="orange.700">
        ${(result.spent ?? 0).toFixed(2)} spent + ${(result.estimateUsd ?? 0).toFixed(2)} estimated = ${(result.projected ?? 0).toFixed(2)} vs ${(result.dailyCap ?? 0).toFixed(2)} cap.
      </Text>
      <Text fontSize="11px" color="orange.700" mt={1}>{result.reason}</Text>
    </Box>
  );
}

function GenericCard({ result }: { result: any }) {
  const [open, setOpen] = useState(false);
  const label = String(result.kind || 'tool result');
  return (
    <Card>
      <CardHeader label={label} />
      <Box
        as="details"
        open={open}
        data-testid="generic-card-details"
        data-open={open ? 'true' : 'false'}
      >
        <Box
          as="summary"
          fontSize="12px"
          color="brand.muted"
          cursor="pointer"
          width="fit-content"
          onClick={(event: React.MouseEvent) => {
            event.preventDefault();
            setOpen((value) => !value);
          }}
        >
          Details
        </Box>
        {open ? (
          <Code
            data-testid="generic-card-json"
            display="block"
            whiteSpace="pre-wrap"
            fontSize="11px"
            p={2}
            mt={2}
            maxH="240px"
            overflowY="auto"
          >
            {JSON.stringify(result.data ?? result, null, 2)}
          </Code>
        ) : null}
      </Box>
    </Card>
  );
}
